import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { 
  FaSearch, FaPlus, FaEdit, FaTrash, FaTimes, 
  FaChevronDown, FaChevronUp, FaMoneyBillWave, FaPaperclip, FaEye 
} from 'react-icons/fa';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';
import './AddExpense.css';

const initialFormState = { 
  particular: '', 
  amount: '', 
  date: '', 
  paymentMode: 'Cash', 
  refNo: '', 
  attachment: null,
  attachmentName: '',
  attachmentPreview: null,
  deleteAttachment: false
};

const AddExpense = () => {
  const [expenses, setExpenses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form & View States
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [editId, setEditId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => { fetchExpenses(); }, []);

  const fetchExpenses = async () => {
    try {
      const res = await axiosInstance.get('/api/expenses');
      setExpenses(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (error) { 
      console.error('Error fetching expenses:', error); 
    }
  };

  const activeExpenses = useMemo(() => {
    let list = expenses.filter(e => !e.isDeleted);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e => e.particular && e.particular.toLowerCase().includes(q));
    }
    return list;
  }, [expenses, searchQuery]);

  const paginatedExpenses = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return activeExpenses.slice(startIndex, startIndex + itemsPerPage);
  }, [activeExpenses, currentPage, itemsPerPage]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm(prev => ({
        ...prev,
        attachment: file,
        attachmentName: file.name,
        attachmentPreview: URL.createObjectURL(file),
        deleteAttachment: false
      }));
    }
  };

  const handleDeleteAttachment = () => {
    setForm(prev => ({
      ...prev,
      attachment: null,
      attachmentName: '',
      attachmentPreview: null,
      deleteAttachment: true
    }));
  };

  const handleSaveExpense = async () => {
    if (!form.particular || !form.amount || !form.date || !form.paymentMode) {
      return Swal.fire('Warning', 'Particular, Amount, Date, and Payment Mode are required', 'warning');
    }
    if (parseFloat(form.amount) <= 0) {
      return Swal.fire('Warning', 'Amount must be greater than zero', 'warning');
    }
    if (form.paymentMode !== 'Cash' && (!form.refNo || form.refNo.trim() === '')) {
      return Swal.fire('Warning', 'Reference number is required for non-Cash payments', 'warning');
    }
    
    try {
      const formData = new FormData();
      formData.append('particular', form.particular.trim());
      formData.append('amount', parseFloat(form.amount));
      formData.append('date', form.date);
      formData.append('paymentMode', form.paymentMode);
      
      if (form.paymentMode !== 'Cash') {
        formData.append('refNo', form.refNo.trim());
      }
      if (form.attachment) {
        formData.append('attachment', form.attachment);
      }
      if (form.deleteAttachment) {
        formData.append('deleteAttachment', 'true');
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      if (editId) {
        await axiosInstance.put(`/api/expenses/${editId}`, formData, config);
        Swal.fire('Updated', 'Expense record updated successfully', 'success');
      } else {
        await axiosInstance.post('/api/expenses', formData, config);
        Swal.fire('Added', 'New expense added successfully', 'success');
      }
      resetForm();
      fetchExpenses();
    } catch (error) { 
      const errorMsg = error.response?.data?.message || 'Operation failed';
      Swal.fire('Error', errorMsg, 'error'); 
    }
  };

  const handleEdit = (e) => { 
    const baseURL = axiosInstance.defaults.baseURL ? axiosInstance.defaults.baseURL.replace(/\/api$/, '') : '';
    const fileUrl = e.attachment ? `${baseURL}/${e.attachment}` : null;
    
    setForm({
      particular: e.particular || '',
      amount: e.amount || '',
      date: e.date ? e.date.split('T')[0] : '',
      paymentMode: e.paymentMode || 'Cash',
      refNo: e.refNo || '',
      attachment: null,
      attachmentName: e.attachment ? e.attachment.split('/').pop() : '',
      attachmentPreview: fileUrl,
      deleteAttachment: false
    }); 
    setEditId(e._id); 
    setShowAddForm(true); 
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  };
  
  const handleDelete = async (id) => {
    const res = await Swal.fire({ 
      title: 'Delete Expense?', 
      text: 'This will mark the expense as deleted.', 
      icon: 'warning', 
      showCancelButton: true, 
      confirmButtonColor: '#e74c3c' 
    });
    if (res.isConfirmed) {
      await axiosInstance.delete(`/api/expenses/${id}`); 
      fetchExpenses();
    }
  };

  const toggleStatus = async (id, status) => { 
    await axiosInstance.put(`/api/expenses/${id}/status`, { isActive: !status }); 
    fetchExpenses(); 
  };

  const resetForm = () => {
    setShowAddForm(false); 
    setForm(initialFormState); 
    setEditId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? dateString : d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatAmount = (amt) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amt || 0);
  };

  const cleanBaseURL = () => {
    const baseURL = axiosInstance.defaults.baseURL || '';
    return baseURL.endsWith('/api') ? baseURL.slice(0, -4) : baseURL;
  };

  const staticBaseURL = cleanBaseURL();

  return (
    <>
    <Helmet>
      <title>Expenses | Rajput Shiksha Kosh</title>
      <meta name="description" content="Manage Expenses for the Rajput Shiksha Kosh organization." />
    </Helmet>
    <div className="admin-add-expenses-container">
      
      <h2 className="admin-add-expenses-title">
        <FaMoneyBillWave style={{marginRight: '8px'}}/> Expense Management
      </h2>
      
      <div className="admin-add-expenses-topbar">
        {/* LEFT */}
        <div className="admin-add-expenses-topbar-left">
          <div className="admin-add-expenses-search-wrapper">
            <FaSearch className="admin-add-expenses-search-icon" />
            <input
              type="text"
              className="admin-add-expenses-search-input"
              placeholder="Search Particulars..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* RIGHT */}
        <div className="admin-add-expenses-topbar-right">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="admin-add-expenses-items-select"
          >
            <option value="5">5 / page</option>
            <option value="10">10 / page</option>
            <option value="20">20 / page</option>
            <option value="30">30 / page</option>
            <option value="50">50 / page</option>
          </select>

          <button
            className="admin-add-expenses-btn"
            onClick={() => showAddForm ? resetForm() : setShowAddForm(true)}
          >
            {showAddForm
              ? <><FaTimes /> Close Form</>
              : <><FaPlus /> Add Expense</>}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="admin-add-expenses-inline-form">
          <h3 className="admin-add-expenses-form-title">{editId ? '✏️ Edit Expense Record' : 'Add New Expense'}</h3>
          
          <div className="admin-add-expenses-form-grid">
            <div className="admin-add-expenses-form-group admin-add-expenses-col-span-full">
              <label>Particular (Description) <span className="admin-add-expenses-req">*</span></label>
              <input type="text" value={form.particular} onChange={e => setForm({...form, particular: e.target.value})} placeholder="Enter expense details/particulars" />
            </div>
            
            <div className="admin-add-expenses-form-group">
              <label>Amount (₹) <span className="admin-add-expenses-req">*</span></label>
              <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" />
            </div>
            
            <div className="admin-add-expenses-form-group">
              <label>Date <span className="admin-add-expenses-req">*</span></label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
            </div>

            <div className="admin-add-expenses-form-group">
              <label>Payment Mode <span className="admin-add-expenses-req">*</span></label>
              <select value={form.paymentMode} onChange={e => setForm({...form, paymentMode: e.target.value, refNo: ''})}>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="NEFT">NEFT</option>
                <option value="RTGS">RTGS</option>
              </select>
            </div>

            {form.paymentMode !== 'Cash' && (
              <div className="admin-add-expenses-form-group">
                <label>Reference No <span className="admin-add-expenses-req">*</span></label>
                <input type="text" value={form.refNo} onChange={e => setForm({...form, refNo: e.target.value})} placeholder="Transaction ID, Cheque No, etc." />
              </div>
            )}

            <div className="admin-add-expenses-form-group admin-add-expenses-col-span-full">
              <label>Attachment (Image File)</label>
              <div className="admin-add-expenses-file-wrapper">
                <input 
                  type="file" 
                  accept="image/*" 
                  id="attachment" 
                  className="admin-add-expenses-file-input" 
                  onChange={handleFileChange} 
                />
                <label htmlFor="attachment" className="admin-add-expenses-file-label">
                  Choose File
                </label>
                <span className="admin-add-expenses-file-name">
                  {form.attachmentName || 'No file chosen'}
                </span>
              </div>
              
              {form.attachmentPreview && (
                <div className="admin-add-expenses-preview-container">
                  <img src={form.attachmentPreview} alt="Receipt preview" className="admin-add-expenses-preview-img" />
                  <button type="button" className="admin-add-expenses-btn-delete-file" onClick={handleDeleteAttachment}>
                    <FaTrash style={{ marginRight: '4px' }} /> Remove Image
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="admin-add-expenses-form-buttons">
            <button className="admin-add-expenses-btn-cancel" onClick={resetForm}>Cancel</button>
            <button className="admin-add-expenses-btn-confirm" onClick={handleSaveExpense}>
              {editId ? 'Update Expense' : 'Save Expense'}
            </button>
          </div>
        </div>
      )}

      <div className="admin-add-expenses-table-wrapper">
        <table className="admin-add-expenses-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th className="admin-add-expenses-align-left">Particular</th>
              <th className="admin-add-expenses-col-date">Date</th>
              <th className="admin-add-expenses-col-amount">Amount</th>
              <th className="admin-add-expenses-col-status">Status</th>
              <th className="admin-add-expenses-col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {activeExpenses.length === 0 ? (
              <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No expense records found.</td></tr>
            ) : paginatedExpenses.map((e, i) => (
              <React.Fragment key={e._id}>
                <tr className="admin-add-expenses-main-row">
                  <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                  
                  <td className="admin-add-expenses-align-left">
                    <div className="admin-add-expenses-name-wrapper" onClick={() => setExpandedRow(expandedRow === e._id ? null : e._id)}>
                      <span className="admin-add-expenses-particular-text">{e.particular}</span>
                      <button className="admin-add-expenses-expand-btn">
                        {expandedRow === e._id ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    </div>
                  </td>
                  
                  <td className="admin-add-expenses-col-date" style={{ fontWeight: 500 }}>{formatDate(e.date)}</td>
                  <td className="admin-add-expenses-col-amount" style={{ fontWeight: 700, color: '#e74c3c' }}>{formatAmount(e.amount)}</td>
                  
                  <td className="admin-add-expenses-col-status">
                    <label className="admin-add-expenses-switch">
                      <input type="checkbox" checked={e.isActive !== false} onChange={() => toggleStatus(e._id, e.isActive !== false)} />
                      <span className="admin-add-expenses-slider" />
                    </label>
                    <div style={{ fontSize: '0.75rem', marginTop: '4px', color: e.isActive !== false ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>
                      {e.isActive !== false ? 'Active' : 'Disabled'}
                    </div>
                  </td>
                  
                  <td className="admin-add-expenses-col-action">
                    <div className="admin-add-expenses-action-btns">
                      <button className="admin-add-expenses-btn-edit" onClick={() => handleEdit(e)} title="Edit"><FaEdit /></button>
                      <button className="admin-add-expenses-btn-delete" onClick={() => handleDelete(e._id)} title="Delete"><FaTrash /></button>
                    </div>
                  </td>
                </tr>
                
                {expandedRow === e._id && (
                  <tr className="admin-add-expenses-expanded-row">
                    <td colSpan="6">
                      <div className="admin-add-expenses-expanded-content">
                        
                        <div className="admin-add-expenses-exp-item admin-add-expenses-exp-date">
                          <span className="admin-add-expenses-exp-label">Date</span>
                          <span className="admin-add-expenses-exp-value">{formatDate(e.date)}</span>
                        </div>
                        
                        <div className="admin-add-expenses-exp-item admin-add-expenses-exp-amount">
                          <span className="admin-add-expenses-exp-label">Amount</span>
                          <span className="admin-add-expenses-exp-value" style={{color: '#e74c3c', fontSize: '1.1rem'}}>{formatAmount(e.amount)}</span>
                        </div>

                        <div className="admin-add-expenses-exp-item">
                          <span className="admin-add-expenses-exp-label">Payment Mode</span>
                          <span className="admin-add-expenses-exp-value">{e.paymentMode || 'Cash'}</span>
                        </div>

                        {e.paymentMode !== 'Cash' && (
                          <div className="admin-add-expenses-exp-item">
                            <span className="admin-add-expenses-exp-label">Reference No</span>
                            <span className="admin-add-expenses-exp-value">{e.refNo || '—'}</span>
                          </div>
                        )}

                        <div className="admin-add-expenses-exp-item admin-add-expenses-exp-attachment-display">
                          <span className="admin-add-expenses-exp-label">Attachment</span>
                          <div className="admin-add-expenses-exp-value">
                            {e.attachment ? (
                              <a 
                                href={`${staticBaseURL}/${e.attachment}`} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="admin-add-expenses-thumbnail-link"
                              >
                                <img 
                                  src={`${staticBaseURL}/${e.attachment}`} 
                                  alt="Receipt attachment" 
                                  className="admin-add-expenses-thumbnail" 
                                />
                                <span className="admin-add-expenses-thumbnail-text">
                                  <FaEye /> View Full Image
                                </span>
                              </a>
                            ) : (
                              <span className="admin-add-expenses-no-attachment">No Attachment</span>
                            )}
                          </div>
                        </div>

                        <div className="admin-add-expenses-exp-item admin-add-expenses-exp-status">
                          <span className="admin-add-expenses-exp-label">Status</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label className="admin-add-expenses-switch">
                              <input type="checkbox" checked={e.isActive !== false} onChange={() => toggleStatus(e._id, e.isActive !== false)} />
                              <span className="admin-add-expenses-slider" />
                            </label>
                            <span style={{ fontSize: '0.85rem', color: e.isActive !== false ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>
                              {e.isActive !== false ? 'Active' : 'Disabled'}
                            </span>
                          </div>
                        </div>

                        <div className="admin-add-expenses-exp-item admin-add-expenses-exp-action">
                          <span className="admin-add-expenses-exp-label">Actions</span>
                          <div className="admin-add-expenses-action-btns" style={{justifyContent: 'flex-start'}}>
                            <button className="admin-add-expenses-btn-edit" onClick={() => handleEdit(e)}><FaEdit/> Edit</button>
                            <button className="admin-add-expenses-btn-delete" onClick={() => handleDelete(e._id)}><FaTrash/> Delete</button>
                          </div>
                        </div>

                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination 
        totalItems={activeExpenses.length}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
    </>
  );
};

export default AddExpense;